/**
 * Ticket-type form — one surface for composing a ticket type, in two modes.
 * `mode="live"` (default) edits a real ticket type under a saved event —
 * `ticketTypesAction$` performs the Firestore write, mirrored from
 * `ticketTypesState`. `mode="draft"` composes a `TicketTypeDraft` locally,
 * for an event that doesn't exist yet — no write, just a `draft-save` event
 * carrying the composed draft back to the caller (event-form.view.ts's
 * create-mode ticket-types card, which owns the draft list). Both modes
 * share every field the draft can carry, sales window and venue override
 * included, and the audience (`claimableBy`) with them: a free type answered
 * nowhere mints staff-only, so an operator who never saw the question would
 * publish a shop with nothing in it. Draft mode offers the two audiences a
 * not-yet-created event can honour — no voucher exists to reveal a code-gated
 * type — and the chips show the fail-safe answer selected until the operator
 * picks another, so what they see is what the mint writes. The door policy
 * (`verifyAtDoor` plus its `doorNote`) stays live-only, the edit-mode-only
 * shape `volumeDiscounts` already has: `TicketTypeDraft` carries neither, and
 * a control whose answer the mint would silently drop is worse than its
 * absence — every type mints scan-and-go, and the operator answers it from
 * this same form once the event exists.
 * Draft mode enters the sales-window override as naive wall-clock strings
 * (no venue, and so no IANA zone, exists yet to resolve a real instant
 * against — the write reconciler converts at mint time, once the created
 * event's own locationId is known), and its inherit label reads generically
 * rather than resolving a concrete date, since the parent event's own
 * schedule is still being composed in event-form.view.ts, not yet a persisted
 * doc `eventsState`/`inheritedWindowLabel()` could read. Mounted via `show()`;
 * closes itself by dispatching `close`.
 *
 * `duplicateOf` opens this same form over a COPY, the name suffixed, `id`
 * left empty — so Save takes the create path (live) or emits a `draft-save`
 * carrying a fresh draftId (draft), and the copy exists only once that Save
 * lands. Never persisted on the gesture itself: TicketTypeSchema has no
 * hidden or draft state, so a clone written on click would be on sale on a
 * published event's storefront at once. What prefills the copy:
 * `duplicateSeed` — the closing form's own field snapshot, riding the close
 * result so the copy carries what the operator SAW, uncommitted edits
 * included — or, from a row-level gesture with no form open to snapshot,
 * the stored row `duplicateOf` names (a real type's id in "live" mode, a
 * draft's draftId in "draft" mode). An editing form's Duplicate button
 * closes this overlay with `{ duplicateOf, seed }` as its result — the old
 * one closes, and the mounting form reopens this one over that result once
 * the close has settled.
 *
 * `showAfterSoldOut` offers only candidates `chainOptions`/`draftChainOptions`
 * already exclude self-reference and cycles from (the "refuse" rule)
 * — error prevention by construction (H5), in both modes: a draft can chain
 * behind a sibling draft by draftId before either is ever saved. A
 * descending chained price warns hard (does not block save — settle
 * enforces capacity and salesWindow regardless of visibility, live mode
 * only); a chained predecessor carrying an explicit sales-window override —
 * live's own `salesWindow.closesAt`, or a draft predecessor's own
 * `salesWindow` — warns that the window closing reveals this type even
 * short of sellout.
 *
 * Languages: `<localized-text>` rides inside the name field and inside the
 * description field, in each one's own `actions` slot, opening that one
 * field's versions and patching that one map through `copyFieldAction$` —
 * never through this form's own Save, which writes the whole row. Each is
 * locked while an unsaved edit to its own field stands, because a version
 * written from a string this form is about to replace is a version its own
 * Save retires. Why a trigger is locked, dead, or standing over a pass that
 * failed is one sentence under its field, and `<localize-notice>` owns which
 * one — the `actions` slot takes controls, and on the single-line name field
 * it is a band with no room for a paragraph at all. Draft mode has no stored
 * string the versions could have been written from, so every trigger there
 * disables itself.
 */

import { formatCurrency } from "@mhmo91/rx";
import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { $notify } from "@mhmo91/schmancy/notification";
import { confirm, show } from "@mhmo91/schmancy/overlay";
import { resolveLocalizedText } from "@momo/i18n";
import {
	DEFAULT_TICKET_TEMPLATE_VARIANT,
	type Dependents,
	Location,
	type TicketTemplateVariant,
	TicketType,
} from "@momo/types";
import { html, type TemplateResult } from "lit";
import { property, state as stateDecorator } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { filter, firstValueFrom, map, takeUntil } from "rxjs";
import { canViewSaleReports } from "../../admin/sale/sale.state.ts";
import { unlockingCodes } from "../../vouchers/vouchers.state.ts";
import "../../catalog/delete-refusal.view.ts";
import { formatAmountInput, parseAmountInput } from "../../i18n/amount.ts";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { locationsState } from "../../storefront/locations.state.ts";
import "../../locations/select-location.view.ts";
import "../../ticket-templates/ticket-template-picker.view.ts";
import { copyFieldSettled$ } from "../localize/copy-field.state.ts";
import type {
	LocalizedTextSaveEvent,
	LocalizedTextTranslateEvent,
} from "../localize/localized-text.view.ts";
import "../localize/localize-notice.view.ts";
import "../localize/localized-text.view.ts";
import {
	draftChainOptions,
	draftChainPredecessor,
	eventsState,
	type TicketTypeDraft,
} from "../events.state.ts";
import {
	chainOptions,
	chainPredecessor,
	chainPredecessorHasClosesAt,
	chainPriceDescends,
	quotaRequest$,
	quotaResult$,
	type TicketTypeWrite,
	ticketTypesAction$,
	ticketTypesResult$,
	ticketTypesState,
} from "./ticket-types.state.ts";

/** The form's own composable answers, as the fields hold them — naive
 *  strings, no instant conversions — so a copy prefills from what the
 *  operator SAW, uncommitted edits included. */
export type TicketTypeFormSeed = {
	kind: TicketType.TicketType["kind"];
	name: string;
	description: string;
	vatRate: string;
	price: string;
	capacity: string;
	claimableBy: NonNullable<TicketType.TicketType["claimableBy"]> | null;
	verifyAtDoor: boolean;
	doorNote: string;
	showAfterSoldOut: string;
	releaseCondition: "always" | "afterSoldOut";
	inheritSalesWindow: boolean;
	opensAt: string;
	closesAt: string;
	entryChoice: "whole" | "days";
	entryOpensAt: string;
	entryClosesAt: string;
	locationId: string;
	templateVariant: TicketTemplateVariant | null;
};

/** The one answer this form's `close` carries beyond "done": reopen me over
 *  this row as a copy, prefilled from `seed` — the closing form's own
 *  fields, so an edit typed but not yet saved rides into the copy instead
 *  of silently reverting to the stored row. */
export type TicketTypeFormResult = {
	duplicateOf: string;
	seed: TicketTypeFormSeed;
};

const KINDS: readonly TicketType.TicketType["kind"][] = ["ticket", "addon"];

/** One UTC day, in milliseconds. The overnight roll and its inverse step the
 *  day KEY in UTC, where no offset ever shifts — adding the same 24h to a
 *  zoned instant repeats or skips a day across a DST boundary. */
const ONE_DAY = 86_400_000;

@schmancyElement("ticket-type-form")
export class TicketTypeForm extends LocalizeMixin(SchmancyElement) {
	@property({ type: String }) mode: "live" | "draft" = "live";
	@property({ type: String }) eventId = "";
	/** Empty means composing new; set means editing that existing thing — a
	 *  real ticket type's own id in "live" mode, a draft's own draftId in
	 *  "draft" mode. Overrides the inherited HTMLElement.id. */
	@property({ type: String }) override id = "";
	/** Empty means nothing; set means compose a NEW type — `id` stays empty —
	 *  prefilled from this source: a real ticket type's own id in "live" mode,
	 *  a draft's own draftId in "draft" mode. */
	@property({ type: String }) duplicateOf = "";
	/** The closing form's own field snapshot to prefill the copy from (set
	 *  beside `duplicateOf`, `id` empty) — what the operator SAW, uncommitted
	 *  edits included. Null falls back to the stored row `duplicateOf` names:
	 *  a row-level gesture has no open form to snapshot. */
	@property({ attribute: false }) duplicateSeed: TicketTypeFormSeed | null =
		null;
	/** "draft" mode only: every local draft on this event (including the one
	 *  being edited, if any) — the chain picker's candidate pool. */
	@property({ attribute: false }) drafts: readonly TicketTypeDraft[] = [];
	/** "draft" mode only: the parent event's own composed schedule, as the
	 *  naive wall-clock strings event-form is holding it in — the days the
	 *  entry chips offer, the way a persisted event's schedule supplies them in
	 *  live mode. Null while the schedule is still blank, which leaves the two
	 *  datetime fields standing in place of a row with no days to draw. */
	@property({ attribute: false }) draftSchedule: {
		start: string;
		end: string;
	} | null = null;

	@stateDecorator() private kind: TicketType.TicketType["kind"] = "ticket";
	@stateDecorator() private name = "";
	/** Optional buyer-facing copy. Empty string is the field's own absent
	 *  state — trimmed to null on save, never written as "". */
	@stateDecorator() private description = "";
	@stateDecorator() private vatRate = "7";
	@stateDecorator() private price = "0";
	@stateDecorator() private capacity = "0";
	/** The operator's own audience answer; null is unanswered, which the
	 *  chips render through TicketType.resolveClaimableBy — so an untouched
	 *  type follows its price (free reads staff-only) and keeps following it
	 *  after a later re-price, while one answer here pins it for good. Holds
	 *  the field's whole answer domain, not only what the chips below offer:
	 *  an answer this form cannot draw a chip for is carried out on Save
	 *  exactly as it came in, never normalised into one that has a chip. */
	@stateDecorator() private claimableBy: NonNullable<
		TicketType.TicketType["claimableBy"]
	> | null = null;
	/** Whether the door holds this type for a person instead of admitting it
	 *  on decode. False is the answer an untouched type carries, not an
	 *  absence — the door reads it as the ordinary scan-and-go policy. */
	@stateDecorator() private verifyAtDoor = false;
	/** The one thing staff is asked to check. Empty string is the field's own
	 *  absent state — trimmed to null on save, never written as "", the same
	 *  shape `description` above keeps. */
	@stateDecorator() private doorNote = "";
	@stateDecorator() private showAfterSoldOut = "";
	/** The "When does this ticket go on sale?" switch's own UI mode — decoupled
	 *  from showAfterSoldOut so toggling to "afterSoldOut" with no predecessor
	 *  picked yet is distinguishable from "always available". */
	@stateDecorator() private releaseCondition: "always" | "afterSoldOut" =
		"always";
	/** Whether this type's sales window is left ABSENT — inheriting the event's
	 *  own, resolved at read time by TicketType.resolveSalesWindow. The safe
	 *  default for a new type: nothing to maintain, and a later event.schedule
	 *  edit keeps propagating. Turning it off is the deliberate override. */
	@stateDecorator() private inheritSalesWindow = true;
	@stateDecorator() private opensAt = "";
	@stateDecorator() private closesAt = "";
	/** "whole" leaves entryWindow ABSENT — admitting on every day of the
	 *  event, the inherit state TicketType.resolveEntryWindow reads — while
	 *  "days" authors an explicit window off the day chips below. The safe
	 *  default for a new type, mirroring inheritSalesWindow's posture. */
	@stateDecorator() private entryChoice: "whole" | "days" = "whole";
	/** The explicit window's two ends, as naive venue wall-clock
	 *  "YYYY-MM-DDTHH:mm" strings — the exact pair entryWindow persists, held
	 *  whole rather than split into days and clocks. Chips, clocks and the
	 *  unfenced datetime pair all write these same two strings, so the answer
	 *  has no second representation to drift from. */
	@stateDecorator() private entryOpensAt = "";
	@stateDecorator() private entryClosesAt = "";
	/** Which end the next chip tap sets, so that a tap always INCLUDES the day
	 *  it tapped. Extend/shrink/collapse forfeits that: on a Mon–Fri span,
	 *  tapping Friday would remove Friday while tapping Wednesday would mean
	 *  "Wednesday only" — one gesture, opposite meanings, with nothing on
	 *  screen saying which is about to fire. */
	@stateDecorator() private entryPicking: "first" | "last" = "first";
	/** The day bounds the stored window arrived with, snapshotted at mount —
	 *  the chip row's own anchor beside the event's schedule, so a window the
	 *  schedule has since moved away from stays offerable for the whole
	 *  session rather than dropping off the row the moment a tap narrows the
	 *  span. Null for a type being authored fresh. */
	private storedEntryDays: { first: string; last: string } | null = null;
	@stateDecorator() private locationId = "";
	/** Rendering-variant override for this type alone — null is the
	 *  inherit-the-event's-own state. */
	@stateDecorator() private templateVariant: TicketTemplateVariant | null =
		null;
	@stateDecorator() private saving = false;
	@stateDecorator() private activeAction: "save" | "delete" | null = null;
	@stateDecorator() private failedAction: "save" | "delete" | null = null;
	/**
	 * The two source strings as they stand on disk, snapshotted at mount. An
	 * edit away from either is what retires THAT field's map: the stored map
	 * was drafted from this exact string, so a different one leaves it
	 * describing copy nobody serves. Per field, because name and description
	 * are edited independently — one document-level flag cannot say "the name
	 * was re-typed in German while the description is still English-sourced".
	 * Empty/null for a type being created: there is no stored map to retire.
	 */
	private storedName = "";
	private storedDescription: string | null = null;

	override connectedCallback(): void {
		super.connectedCallback();
		// A copy opens under a suffixed name, so the list never shows two rows
		// reading alike and the one to retitle is the marked one.
		const source = this.id || this.duplicateOf;
		// The closing form's snapshot outranks the stored row: a duplicate
		// taken mid-edit copies the price the operator was looking at, not the
		// one on disk. The stored pair stays ""/null — no document stands
		// behind the copy, so its first Save writes both maps as null.
		if (!this.id && this.duplicateSeed) {
			const seed = this.duplicateSeed;
			this.kind = seed.kind;
			this.name = this.t("admin.ticketTypes.form.duplicateName", {
				name: seed.name,
			});
			this.description = seed.description;
			this.vatRate = seed.vatRate;
			this.price = seed.price;
			this.capacity = seed.capacity;
			this.claimableBy = seed.claimableBy;
			this.verifyAtDoor = seed.verifyAtDoor;
			this.doorNote = seed.doorNote;
			this.showAfterSoldOut = seed.showAfterSoldOut;
			this.releaseCondition = seed.releaseCondition;
			this.inheritSalesWindow = seed.inheritSalesWindow;
			this.opensAt = seed.opensAt;
			this.closesAt = seed.closesAt;
			this.entryChoice = seed.entryChoice;
			if (
				seed.entryChoice === "days" &&
				seed.entryOpensAt &&
				seed.entryClosesAt
			) {
				this.hydrateEntryWindow(seed.entryOpensAt, seed.entryClosesAt);
			}
			this.locationId = seed.locationId;
			this.templateVariant = seed.templateVariant;
			return;
		}
		if (this.mode === "draft") {
			const draft = source
				? this.drafts.find((d) => d.draftId === source)
				: undefined;
			if (!draft) return;
			this.kind = draft.kind;
			this.name = this.id
				? draft.name
				: this.t("admin.ticketTypes.form.duplicateName", { name: draft.name });
			this.description = draft.description ?? "";
			this.vatRate = String(draft.vatRate);
			this.price = formatAmountInput(draft.price / 100, this.uiLanguage, 2);
			this.capacity = String(draft.capacity);
			this.claimableBy = draft.claimableBy;
			this.showAfterSoldOut = draft.showAfterSoldOut ?? "";
			this.releaseCondition = draft.showAfterSoldOut
				? "afterSoldOut"
				: "always";
			this.locationId = draft.locationId ?? "";
			this.templateVariant = draft.templateVariant ?? null;
			// Already naive wall-clock strings — no instant→wall-clock conversion
			// needed (unlike live mode below): a draft's override was never
			// resolved against a venue's zone in the first place.
			this.inheritSalesWindow = !draft.salesWindow;
			if (draft.salesWindow) {
				this.opensAt = draft.salesWindow.opensAt;
				this.closesAt = draft.salesWindow.closesAt;
			}
			if (draft.entryWindow) {
				this.hydrateEntryWindow(
					draft.entryWindow.opensAt,
					draft.entryWindow.closesAt,
				);
			}
			return;
		}
		const canonical = source
			? ticketTypesState.value.find((t) => t.id === source)
			: undefined;
		if (!canonical) return;
		this.kind = canonical.kind;
		this.name = this.id
			? canonical.name
			: this.t("admin.ticketTypes.form.duplicateName", {
					name: canonical.name,
				});
		this.description = canonical.description ?? "";
		// A copy has no document behind it, so its stored pair stays empty/null
		// and its first Save writes both maps as null — a create's own shape.
		// Trimmed, because save() compares against the value it is about to
		// write, which is itself trimmed — a stored string carrying a trailing
		// newline (pasted copy, a provisioning script, a seed) would otherwise
		// read as edited on an untouched mount and retire a map nobody asked to
		// retire.
		if (this.id) {
			this.storedName = canonical.name.trim();
			this.storedDescription = canonical.description?.trim() || null;
		}
		this.vatRate = String(canonical.vatRate);
		this.price = formatAmountInput(canonical.price / 100, this.uiLanguage, 2);
		this.capacity = String(canonical.capacity);
		this.claimableBy = canonical.claimableBy ?? null;
		this.verifyAtDoor = canonical.verifyAtDoor;
		this.doorNote = canonical.doorNote ?? "";
		this.showAfterSoldOut = canonical.showAfterSoldOut ?? "";
		this.releaseCondition = canonical.showAfterSoldOut
			? "afterSoldOut"
			: "always";
		this.locationId = canonical.locationId ?? "";
		this.templateVariant = canonical.templateVariant ?? null;
		// The RAW field decides the switch — an absent salesWindow is the
		// inherit state itself, not a value to be filled in. Deliberately NOT
		// resolveSalesWindow here: loading the resolved window into the fields
		// would save it back as this type's own on the next edit, and
		// ticket-type.entity.ts locks the opposite ("the resolved value is never
		// persisted back to this field, so a later event.schedule edit keeps
		// propagating to every type still inheriting"). Only an explicit
		// override has instants to show, read against the same locationId FK
		// chain (type override, else the event's own) resolveSalesWindow follows.
		this.inheritSalesWindow = !canonical.salesWindow;
		if (!canonical.salesWindow && !canonical.entryWindow) return;
		const event = eventsState.value.find((e) => e.id === this.eventId);
		if (!event) return;
		const locations = Object.fromEntries(
			locationsState.value.map((l) => [l.id, l] as const),
		);
		const tz = TicketType.resolveEffectiveTimezone(canonical, event, locations);
		if (canonical.salesWindow) {
			this.opensAt = Location.instantToWallClock(
				canonical.salesWindow.opensAt.toDate(),
				tz,
			);
			this.closesAt = Location.instantToWallClock(
				canonical.salesWindow.closesAt.toDate(),
				tz,
			);
		}
		if (canonical.entryWindow) {
			this.hydrateEntryWindow(
				Location.instantToWallClock(canonical.entryWindow.opensAt.toDate(), tz),
				Location.instantToWallClock(
					canonical.entryWindow.closesAt.toDate(),
					tz,
				),
			);
		}
	}

	/** The stored window as this form holds it: the naive pair itself, plus the
	 *  day bounds it arrived with, which anchor the chip row for the session. */
	private hydrateEntryWindow(opensAt: string, closesAt: string): void {
		this.entryChoice = "days";
		this.entryOpensAt = opensAt;
		this.entryClosesAt = closesAt;
		this.storedEntryDays = {
			first: opensAt.slice(0, 10),
			last: closesAt.slice(0, 10),
		};
	}

	/** The picked days and clocks, restated as the naive pair entryWindow
	 *  persists. A close at or before the open is a night crossing midnight, so
	 *  the door shuts the FOLLOWING morning: one lit chip plus 22:00–02:00 says
	 *  "one night". Without the roll that pair is a window the save path
	 *  refuses, and the operator has to light a second day to say it — which
	 *  then claims the ticket admits all of that second day. */
	private setEntryWindow(
		firstDay: string,
		lastDay: string,
		from: string,
		to: string,
	): void {
		const openTime = from || "00:00";
		const closeTime = to || "23:59";
		this.entryOpensAt = `${firstDay}T${openTime}`;
		this.entryClosesAt = `${
			closeTime <= openTime
				? new Date(Date.parse(`${lastDay}T00:00:00.000Z`) + ONE_DAY)
						.toISOString()
						.slice(0, 10)
				: lastDay
		}T${closeTime}`;
	}

	/** The event's own window, in the venue's wall clock — what an inheriting
	 *  type actually sells by, stated plainly rather than left implicit. */
	private inheritedWindowLabel(): string | null {
		const event = eventsState.value.find((e) => e.id === this.eventId);
		if (!event) return null;
		const locations = Object.fromEntries(
			locationsState.value.map((l) => [l.id, l] as const),
		);
		const tz = TicketType.resolveEffectiveTimezone(
			{ locationId: this.locationId || null },
			event,
			locations,
		);
		// The placeholder previews resolveSalesWindow over the form's own
		// UNSAVED entry answer: an inheriting sale closes at the ENTRY end (the
		// resolver reads resolveEntryWindow's close), so a "days" pick above
		// narrows this label the moment it is made — quoting event-end here
		// would claim the type sells until the event ends when it stops when the
		// door closes. A dst-gap reading falls through to the event's own bound;
		// the save path surfaces it.
		const entryCloses =
			this.entryChoice === "days" && this.entryClosesAt
				? Location.wallClockToInstant(this.entryClosesAt, tz)
				: null;
		const ownEntryEnd = entryCloses instanceof Date ? entryCloses : null;
		const closes = (
			ownEntryEnd ??
			TicketType.resolveSalesWindow(
				{ salesWindow: null, entryWindow: null },
				event,
			).closesAt.toDate()
		).toLocaleString(undefined, {
			day: "numeric",
			month: "short",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			timeZone: tz,
		});
		return this.t(
			ownEntryEnd
				? "admin.ticketTypes.form.inheritedWindowUntilEntryEnd"
				: "admin.ticketTypes.form.inheritedWindowLabel",
			{ date: closes },
		);
	}

	/** `result` rides out as the overlay's own emission, which lands only
	 *  after the closing overlay has released its focus trap — so a copy
	 *  opened on it inerts the page and captures its own focus to return to,
	 *  where one opened on a second event fired mid-close inherits neither. */
	private close(result?: TicketTypeFormResult): void {
		this.dispatchEvent(
			new CustomEvent("close", {
				detail: result,
				bubbles: true,
				composed: true,
			}),
		);
	}

	/** The seed is the fields as they stand RIGHT NOW, not the stored row —
	 *  a duplicate taken mid-edit must carry the price the operator sees. */
	private duplicate(): void {
		this.close({
			duplicateOf: this.id,
			seed: {
				kind: this.kind,
				name: this.name,
				description: this.description,
				vatRate: this.vatRate,
				price: this.price,
				capacity: this.capacity,
				claimableBy: this.claimableBy,
				verifyAtDoor: this.verifyAtDoor,
				doorNote: this.doorNote,
				showAfterSoldOut: this.showAfterSoldOut,
				releaseCondition: this.releaseCondition,
				inheritSalesWindow: this.inheritSalesWindow,
				opensAt: this.opensAt,
				closesAt: this.closesAt,
				entryChoice: this.entryChoice,
				entryOpensAt: this.entryOpensAt,
				entryClosesAt: this.entryClosesAt,
				locationId: this.locationId,
				templateVariant: this.templateVariant,
			},
		});
	}

	/**
	 * One hand-corrected map, patched onto this ticket type's document alone —
	 * never through this form's own Save, which writes the whole row from
	 * fields the operator may still be editing, and which retires the very map
	 * a correction just wrote whenever its source string moved.
	 */
	private async storeCopy(
		target: "type-name" | "type-description",
		event: LocalizedTextSaveEvent,
	): Promise<void> {
		const ok = await firstValueFrom(
			copyFieldSettled$({
				gesture: "correct",
				target,
				eventId: this.eventId,
				ticketTypeId: this.id,
				byLocale: event.detail.byLocale,
			}).pipe(takeUntil(this.disconnecting)),
		).catch(() => false);
		event.detail.settle(ok);
	}

	/** The operator asked, from inside the versions overlay, for one of this
	 *  type's two fields to be written again. One field write and nothing
	 *  beside it — asking twice, or asking during a pass, costs nothing on the
	 *  other side. */
	private async requestPass(
		target: "type-name" | "type-description",
		event: LocalizedTextTranslateEvent,
	): Promise<void> {
		const ok = await firstValueFrom(
			copyFieldSettled$({
				gesture: "translate",
				target,
				eventId: this.eventId,
				ticketTypeId: this.id,
			}).pipe(takeUntil(this.disconnecting)),
		).catch(() => false);
		event.detail.settle(ok);
	}

	private async save(): Promise<void> {
		if (this.saving) return;
		const name = this.name.trim();
		// Absence is null, never "" — an empty string would make every surface
		// render the described branch for a type carrying no copy at all.
		const description = this.description.trim() || null;
		if (!name) {
			$notify.error(this.t("admin.ticketTypes.form.validation.nameRequired"));
			return;
		}
		const vatRate = Number(this.vatRate);
		if (!Number.isFinite(vatRate) || vatRate < 0 || vatRate > 100) {
			$notify.error(this.t("admin.ticketTypes.form.validation.vatRange"));
			return;
		}
		const priceEuros =
			this.price.trim() === ""
				? 0
				: parseAmountInput(this.price, this.uiLanguage);
		if (priceEuros === null) {
			$notify.error(this.t("admin.ticketTypes.form.validation.priceNegative"));
			return;
		}
		const capacity = Number(this.capacity);
		if (!Number.isInteger(capacity) || capacity < 0) {
			$notify.error(
				this.t("admin.ticketTypes.form.validation.capacityInteger"),
			);
			return;
		}
		if (this.releaseCondition === "afterSoldOut" && !this.showAfterSoldOut) {
			$notify.error(
				this.t("admin.ticketTypes.form.validation.predecessorRequired"),
			);
			return;
		}
		// The entry answer, assembled as naive venue wall-clock strings before
		// the mode fork — only the naive→instant conversion is mode-specific (a
		// draft has no venue, so no zone, to resolve against yet). "whole"
		// writes ABSENCE, never a copy of the event's schedule — the copy is
		// what would stop tracking a later event.schedule edit. The order check
		// string-compares: naive "YYYY-MM-DDTHH:mm" sorts chronologically while
		// both strings share the format.
		let entryNaive: { opensAt: string; closesAt: string } | null = null;
		if (this.entryChoice === "days") {
			if (!this.entryOpensAt || !this.entryClosesAt) {
				$notify.error(
					this.t("admin.ticketTypes.form.validation.entryDaysRequired"),
				);
				return;
			}
			entryNaive = {
				opensAt: this.entryOpensAt,
				closesAt: this.entryClosesAt,
			};
			if (entryNaive.closesAt <= entryNaive.opensAt) {
				$notify.error(
					this.t("admin.ticketTypes.form.validation.entryWindowOrder"),
				);
				return;
			}
		}

		if (this.mode === "draft") {
			// Inheriting writes ABSENCE, never a copy of the event's window —
			// mirrors live mode's own rule below. String-compare, not a real Date:
			// the naive "YYYY-MM-DDTHH:mmT" format sorts chronologically as long as
			// both strings share it, and no venue (so no zone) exists yet to
			// resolve a real instant against — that conversion, and its DST-gap
			// check, happen at mint time in the write reconciler.
			let salesWindow: { opensAt: string; closesAt: string } | null = null;
			if (!this.inheritSalesWindow) {
				if (!this.opensAt || !this.closesAt) {
					$notify.error(
						this.t("admin.ticketTypes.form.validation.salesWindowRequired"),
					);
					return;
				}
				if (this.closesAt <= this.opensAt) {
					$notify.error(
						this.t("admin.ticketTypes.form.validation.salesWindowOrder"),
					);
					return;
				}
				salesWindow = { opensAt: this.opensAt, closesAt: this.closesAt };
			}
			this.dispatchEvent(
				new CustomEvent<TicketTypeDraft>("draft-save", {
					detail: {
						draftId: this.id || crypto.randomUUID(),
						kind: this.kind,
						name,
						description,
						vatRate,
						price: Math.round(priceEuros * 100),
						capacity,
						claimableBy:
							this.claimableBy === "buyer" || this.claimableBy === "staff"
								? this.claimableBy
								: null,
						showAfterSoldOut: this.showAfterSoldOut || null,
						locationId: this.locationId || null,
						templateVariant: this.templateVariant,
						salesWindow,
						entryWindow: entryNaive,
					},
					bubbles: true,
					composed: true,
				}),
			);
			this.close();
			return;
		}

		// Read fresh at save time — a venue-override change after typing the
		// times resolves the naive strings under THAT venue's zone.
		const event = eventsState.value.find((e) => e.id === this.eventId);
		const locations = Object.fromEntries(
			locationsState.value.map((l) => [l.id, l] as const),
		);
		const tz = TicketType.resolveEffectiveTimezone(
			{ locationId: this.locationId || null },
			{ locationId: event?.locationId ?? "" },
			locations,
		);
		// Inheriting writes ABSENCE, never a copy of the event's window — that
		// copy is what would stop tracking a later event.schedule edit.
		let salesWindow: { opensAt: Date; closesAt: Date } | null = null;
		if (!this.inheritSalesWindow) {
			if (!this.opensAt || !this.closesAt) {
				$notify.error(
					this.t("admin.ticketTypes.form.validation.salesWindowRequired"),
				);
				return;
			}
			const opensAt = Location.wallClockToInstant(this.opensAt, tz);
			const closesAt = Location.wallClockToInstant(this.closesAt, tz);
			if ("error" in opensAt || "error" in closesAt) {
				$notify.error(
					this.t("admin.ticketTypes.form.validation.invalidTime", {
						time: "error" in opensAt ? this.opensAt : this.closesAt,
						tz,
					}),
				);
				return;
			}
			if (closesAt.getTime() <= opensAt.getTime()) {
				$notify.error(
					this.t("admin.ticketTypes.form.validation.salesWindowOrder"),
				);
				return;
			}
			salesWindow = { opensAt, closesAt };
		}
		let entryWindow: { opensAt: Date; closesAt: Date } | null = null;
		if (entryNaive) {
			const opensAt = Location.wallClockToInstant(entryNaive.opensAt, tz);
			const closesAt = Location.wallClockToInstant(entryNaive.closesAt, tz);
			if ("error" in opensAt || "error" in closesAt) {
				$notify.error(
					this.t("admin.ticketTypes.form.validation.invalidTime", {
						time: "error" in opensAt ? entryNaive.opensAt : entryNaive.closesAt,
						tz,
					}),
				);
				return;
			}
			entryWindow = { opensAt, closesAt };
		}
		// undefined leaves a stored map exactly as it is (updateDoc merges, and
		// the client ignores undefined properties); null retires it.
		const nameByLocale = name === this.storedName ? undefined : null;
		const descriptionByLocale =
			description === this.storedDescription ? undefined : null;
		const body: TicketTypeWrite = {
			kind: this.kind,
			name,
			nameByLocale,
			description,
			descriptionByLocale,
			vatRate,
			price: Math.round(priceEuros * 100),
			capacity,
			// An edit carries the type's own stored position forward untouched; a
			// create (including a duplicate) lands at the current end of the order
			// rather than defaulting to 0 and jumping to the front of the list.
			order: this.id
				? (ticketTypesState.value.find((t) => t.id === this.id)?.order ?? 0)
				: ticketTypesState.value.reduce(
						(max, t) => Math.max(max, t.order),
						-1,
					) + 1,
			showAfterSoldOut: this.showAfterSoldOut || null,
			claimableBy: this.claimableBy,
			verifyAtDoor: this.verifyAtDoor,
			doorNote: this.doorNote.trim() || null,
			volumeDiscounts: [],
			salesWindow,
			entryWindow,
			locationId: this.locationId || null,
			templateVariant: this.templateVariant,
		};

		// Claimed before the confirm, not after it: the confirm is awaited, and a
		// second Save press while it stands would otherwise open a second one.
		this.saving = true;
		this.activeAction = "save";
		try {
			if (!(await this.confirmSoldConsequences(body))) {
				this.saving = false;
				return;
			}

			const requestId = crypto.randomUUID();
			const resultPromise = firstValueFrom(
				ticketTypesResult$.pipe(
					filter((r) => r.requestId === requestId),
					takeUntil(this.disconnecting),
				),
			);
			ticketTypesAction$.next(
				this.id
					? {
							kind: "update",
							requestId,
							eventId: this.eventId,
							id: this.id,
							body,
						}
					: { kind: "create", requestId, eventId: this.eventId, body },
			);
			const result = await resultPromise;
			if (!result.ok) {
				this.failedAction = "save";
				return;
			}
			this.close();
		} catch {
			this.failedAction = "save";
		} finally {
			this.saving = false;
		}
	}

	/**
	 * Names what a price, capacity or NAME edit does to what is already sold,
	 * and refuses nothing — `OrderLine.unitPrice` is frozen on the placed
	 * order and settle re-reads capacity live, so no edit here can move
	 * settled money or take a seat back off anyone. Returns true to proceed.
	 *
	 * Name is the one that is not obvious: the receipt and the § 14c
	 * correction both re-read the live ticket type when they RENDER, so a
	 * rename rewrites the line description on every fiscal document not yet
	 * issued while the ones already sent keep the old wording — for an add-on
	 * exactly as for a ticket, since an invoice line resolves its name from
	 * whichever type its own `typeId` names.
	 *
	 * The figure is this type's own `quota/inventory` consumed count, asked
	 * for at save time and answered by the reconciler: the same doc the delete
	 * door probes, so the confirm and the refusal cannot state different
	 * numbers about one type, and it exists per type whichever kind it is and
	 * whichever screen this form was opened from. It is gated on
	 * `tickets.reports.view`, so a member holding `tickets.events.manage`
	 * alone reads null and gets no confirm — a stated count nobody could read
	 * would be a fabrication.
	 */
	private async confirmSoldConsequences(
		body: TicketTypeWrite,
	): Promise<boolean> {
		const existing = this.id
			? ticketTypesState.value.find((t) => t.id === this.id)
			: undefined;
		if (!existing || !canViewSaleReports.get()) return true;
		const requestId = crypto.randomUUID();
		const consumed = firstValueFrom(
			quotaResult$.pipe(
				filter((r) => r.requestId === requestId),
				map((r) => r.consumed),
				takeUntil(this.disconnecting),
			),
		);
		quotaRequest$.next({ requestId, eventId: this.eventId, typeId: this.id });
		const sold = await consumed;
		if (sold === null || sold === 0) return true;

		const capacityLine =
			body.capacity === existing.capacity
				? null
				: body.capacity <= sold
					? this.t("admin.ticketTypes.form.soldConfirmCapacityBelow", {
							capacity: body.capacity,
							sold,
						})
					: this.t("admin.ticketTypes.form.soldConfirmCapacity", {
							oldCapacity: existing.capacity,
							newCapacity: body.capacity,
							remaining: body.capacity - sold,
						});
		const consequences = [
			body.price === existing.price
				? null
				: this.t(
						sold === 1
							? "admin.ticketTypes.form.soldConfirmPriceOne"
							: "admin.ticketTypes.form.soldConfirmPriceOther",
						{
							count: sold,
							oldPrice: formatCurrency(
								existing.price / 100,
								"EUR",
								this.locale,
							),
							newPrice: formatCurrency(body.price / 100, "EUR", this.locale),
						},
					),
			capacityLine,
			body.name === existing.name
				? null
				: this.t("admin.ticketTypes.form.soldConfirmName", {
						oldName: existing.name,
						newName: body.name,
					}),
		].filter((line) => line !== null);
		if (consequences.length === 0) return true;

		return await confirm({
			title: this.t(
				sold === 1
					? "admin.ticketTypes.form.soldConfirmTitleOne"
					: "admin.ticketTypes.form.soldConfirmTitleOther",
				{ count: sold },
			),
			message: consequences.join(" "),
			confirmText: this.t("admin.common.saveChanges"),
			cancelText: this.t("admin.common.keepEditing"),
		});
	}

	private openDeleteRefusal(dependents: Dependents.Dependents): void {
		show(
			() =>
				html`<delete-refusal entity="ticketType" .dependents=${dependents}></delete-refusal>`,
			{
				sticky: true,
				ariaLabel: this.t("admin.ticketTypes.form.deleteRefusalTitle"),
			},
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	/**
	 * Named to avoid colliding with the inherited Element.prototype.remove().
	 *
	 * The confirm stays here, unlike the event form's own Delete: this form
	 * can see no reliable predictor of the door's answer. The console's sold
	 * figure counts order-line quantity on live orders, while the door refuses
	 * on ticket documents and drawn capacity — a fully refunded type reads
	 * zero sold and still refuses, because its cancelled tickets and its order
	 * lines go on naming it. So the confirm asks, and the door's own census is
	 * what answers.
	 */
	private async deleteTicketType(): Promise<void> {
		if (!this.id || this.saving) return;
		const id = this.id;
		this.saving = true;
		this.activeAction = "delete";
		try {
			const ok = await confirm({
				title: this.t("admin.ticketTypes.form.deleteConfirmTitle"),
				message: this.t("admin.common.confirmCannotUndo"),
				confirmText: this.t("admin.common.delete"),
				cancelText: this.t("admin.common.cancel"),
				variant: "danger",
			});
			if (!ok) return;
			const requestId = crypto.randomUUID();
			const resultPromise = firstValueFrom(
				ticketTypesResult$.pipe(
					filter((r) => r.requestId === requestId),
					takeUntil(this.disconnecting),
				),
			);
			ticketTypesAction$.next({
				kind: "delete",
				requestId,
				eventId: this.eventId,
				id,
			});
			const result = await resultPromise;
			if (result.dependents) {
				this.openDeleteRefusal(result.dependents);
				return;
			}
			if (!result.ok) {
				this.failedAction = "delete";
				return;
			}
			this.close();
		} catch {
			this.failedAction = "delete";
		} finally {
			this.saving = false;
		}
	}

	override render(): TemplateResult {
		const editing = !!this.id;
		const showDeleteButton = this.mode === "live" && editing;
		// The row as it stands ON DISK — the two strings the stored maps were
		// drafted from, and the maps themselves, read live so a correction that
		// lands flows straight back into the trigger beside the field. A draft
		// has no document yet, so it has neither.
		const stored =
			this.mode === "live" && editing
				? ticketTypesState.value.find((t) => t.id === this.id)
				: undefined;
		const nameUnsaved = this.name.trim() !== this.storedName;
		const descriptionUnsaved =
			(this.description.trim() || null) !== this.storedDescription;
		// An ask sits at `requested` until the trigger picks it up, which is a
		// round trip the operator is watching the button through: both states
		// are one pass in progress as far as this surface is concerned.
		const namePassRunning =
			stored?.nameByLocaleStatus?.state === "requested" ||
			stored?.nameByLocaleStatus?.state === "translating";
		const descriptionPassRunning =
			stored?.descriptionByLocaleStatus?.state === "requested" ||
			stored?.descriptionByLocaleStatus?.state === "translating";
		const canSave = this.name.trim().length > 0;
		const isDraftMode = this.mode === "draft";
		// A live candidate's label resolves to the language this operator reads;
		// a draft's is the string they just typed themselves, which has no map
		// and no other language to resolve to.
		const chainCandidates: readonly { id: string; name: string }[] = isDraftMode
			? draftChainOptions(this.id || null, this.drafts).map((d) => ({
					id: d.draftId,
					name: d.name,
				}))
			: chainOptions(this.id || null).map((t) => ({
					id: t.id,
					name: resolveLocalizedText(t.nameByLocale, t.name, this.uiLanguage),
				}));
		const livePredecessor =
			!isDraftMode && this.showAfterSoldOut
				? chainPredecessor(this.showAfterSoldOut)
				: undefined;
		const draftPredecessor =
			isDraftMode && this.showAfterSoldOut
				? draftChainPredecessor(this.showAfterSoldOut, this.drafts)
				: undefined;
		const predecessorPrice = livePredecessor?.price ?? draftPredecessor?.price;
		const priceEuros = parseAmountInput(this.price, this.uiLanguage);
		const priceDescends =
			predecessorPrice !== undefined &&
			priceEuros !== null &&
			chainPriceDescends(Math.round(priceEuros * 100), predecessorPrice);
		// Read as submit reads it: an empty field saves as free, and a figure
		// submit refuses is no price yet, so no free-type hint claims it. The
		// audience still resolves as free then — the fail-safe staff answer.
		const priceCents =
			this.price.trim() === ""
				? 0
				: priceEuros === null
					? null
					: Math.round(priceEuros * 100);
		const claimAudience = TicketType.resolveClaimableBy({
			price: priceCents ?? 0,
			claimableBy: this.claimableBy,
		});
		// The configuration that earns the door question: a €0 type the public
		// shop hands to anyone with the link, where a decode is the only thing
		// standing between the link and the room.
		const freeToAnyone = priceCents === 0 && claimAudience === "buyer";
		// The fact this form is uniquely placed to state: vouchers live under the
		// event (`events/{eventId}/vouchers/{code}`), so the codes that could
		// reveal this type are exactly this event's own, already carried by the
		// listener the console opened. Empty is the state the hint exists to make
		// noticeable — a code-gated type no code reaches is invisible to
		// everyone, and nothing else in the product is positioned to say so.
		const unlockedBy = this.id ? unlockingCodes(this.id) : [];
		const predecessorHasClosesAt =
			(livePredecessor !== undefined &&
				chainPredecessorHasClosesAt(livePredecessor)) ||
			(draftPredecessor !== undefined && draftPredecessor.salesWindow !== null);
		// The entry chips and the sells-a-day-already-over warning both read the
		// event's bounds in the venue's own wall clock — in draft mode from the
		// parent form's composed schedule, which is already naive wall-clock
		// strings.
		const event = eventsState.value.find((e) => e.id === this.eventId);
		const locations = Object.fromEntries(
			locationsState.value.map((l) => [l.id, l] as const),
		);
		const tz = TicketType.resolveEffectiveTimezone(
			{ locationId: this.locationId || null },
			{ locationId: event?.locationId ?? "" },
			locations,
		);
		// The event's own first and last DAY: entry may open earlier in the day
		// than the event's own start hour, so it is the day the schedule falls
		// on that bounds the row, never the hour. Empty is the unanswered day —
		// draft mode's composed schedule is blank until the operator dates the
		// event, and nothing below slices an absent bound.
		const entryScheduleFirstDay = isDraftMode
			? (this.draftSchedule?.start ?? "").slice(0, 10)
			: event
				? Location.instantToWallClock(event.schedule.start.toDate(), tz).slice(
						0,
						10,
					)
				: "";
		const entryScheduleLastDay = isDraftMode
			? (this.draftSchedule?.end ?? "").slice(0, 10)
			: event
				? Location.instantToWallClock(event.schedule.end.toDate(), tz).slice(
						0,
						10,
					)
				: "";
		// Absent stored bounds fall to the schedule's own, so the union below is
		// then the schedule alone.
		const entryAnchorFirst =
			this.storedEntryDays?.first ?? entryScheduleFirstDay;
		const entryAnchorLast = this.storedEntryDays?.last ?? entryScheduleLastDay;
		// The union, because resolveEntryWindow returns an explicit window
		// untouched and never writes it back, so the schedule moves underneath
		// one routinely: offering the schedule alone drops the stored end off
		// the row and the first tap silently rewrites the window. A schedule is
		// what the row needs to EXIST, though — with none, every chip it could
		// draw is already lit and the row can only restate the answer it was
		// handed, so the datetime pair, which can name any day, stands instead.
		const entryDays =
			entryScheduleFirstDay === ""
				? []
				: Location.daysBetween(
						entryAnchorFirst < entryScheduleFirstDay
							? entryAnchorFirst
							: entryScheduleFirstDay,
						entryAnchorLast > entryScheduleLastDay
							? entryAnchorLast
							: entryScheduleLastDay,
					);
		// Read off the stored pair, never defaulted: a stored 18:00→02:00 night
		// mounts showing its own clocks, where a literal 00:00/23:59 would
		// silently widen it on the next Save. Only a type being authored fresh
		// falls to the whole day.
		const entryFrom = this.entryOpensAt.slice(11) || "00:00";
		const entryTo = this.entryClosesAt.slice(11) || "23:59";
		const entryOpensDay = this.entryOpensAt.slice(0, 10);
		// The exact inverse of setEntryWindow's roll: a close at or before the
		// open was rolled a day forward when it was written, so the day it
		// rolled FROM is the last day the row lights. Read straight, a stored
		// club night would light the morning after as a day it admits on.
		const entryLastDay =
			this.entryClosesAt === ""
				? ""
				: entryTo <= entryFrom
					? new Date(
							Date.parse(`${this.entryClosesAt.slice(0, 10)}T00:00:00.000Z`) -
								ONE_DAY,
						)
							.toISOString()
							.slice(0, 10)
					: this.entryClosesAt.slice(0, 10);
		const entryRange =
			entryOpensDay === ""
				? []
				: Location.daysBetween(entryOpensDay, entryLastDay);
		// The draft mount passes no eventId, so no venue resolves and the zone
		// above is the catalog's DEFAULT rather than this event's own. The days
		// and clocks are true in either case; the zone is not, so the sentence
		// names it only where the same FK chain resolveEffectiveTimezone follows
		// actually lands on a venue.
		const entryZoneKnown =
			locations[this.locationId || event?.locationId || ""]?.timezone !==
			undefined;
		// Both sides of the warning compare are naive wall-clock strings in the
		// same effective venue zone, so plain string comparison is
		// chronological. "whole" resolves the entry end to the event's own —
		// resolveEntryWindow's inherit bound.
		const entryClosesNaive =
			this.entryChoice === "days"
				? this.entryClosesAt || null
				: isDraftMode
					? this.draftSchedule?.end || null
					: event
						? Location.instantToWallClock(event.schedule.end.toDate(), tz)
						: null;
		const salesClosesAfterEntryEnds =
			!this.inheritSalesWindow &&
			!!this.closesAt &&
			entryClosesNaive !== null &&
			this.closesAt > entryClosesNaive;
		// The day key IS the venue's calendar day already, so it is read in UTC
		// and never re-zoned — the same reading the chips below take, so a label
		// beside a chip can never name a different day than the chip does.
		const entryDayLabel = (day: string) =>
			new Date(`${day}T00:00:00.000Z`).toLocaleDateString(this.locale, {
				weekday: "short",
				day: "numeric",
				month: "short",
				timeZone: "UTC",
			});

		return html`
			<!-- Footer lives OUTSIDE the scroll container (the location-form.view.ts
			     pattern) so it can never overlay the content nor strand dead space
			     under itself the way a sticky last-child does. -->
			<div class="flex flex-col min-w-[min(90vw,28rem)] max-h-[85vh]">
			<div ?inert=${this.saving} ${overflowWithin()} class="flex flex-col gap-4 p-4">
				<div class="flex flex-col gap-2">
					<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
						${this.t("admin.ticketTypes.form.whatIsThisLabel")}
					</schmancy-typography>
					<schmancy-chips
						class="w-full"
						required
						.value=${this.kind}
						@change=${(e: CustomEvent<{ value: string }>) => (this.kind = e.detail.value as TicketType.TicketType["kind"])}
					>
						${repeat(
							KINDS,
							(k) => k,
							(k) =>
								html`<schmancy-filter-chip .value=${k}
									>${this.t(
										k === "ticket"
											? "admin.ticketTypes.form.kindTicket"
											: "admin.ticketTypes.form.kindAddon",
									)}</schmancy-filter-chip
								>`,
						)}
					</schmancy-chips>
					<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
						${this.t("admin.ticketTypes.form.typeHint")}
					</schmancy-typography>
				</div>

				<schmancy-input
					label=${this.t("admin.ticketTypes.form.nameLabel")}
					required
					hint=${this.t("admin.ticketTypes.form.nameHint")}
					.value=${this.name}
					@input=${(e: CustomEvent<{ value: string }>) => (this.name = e.detail.value)}
				>
					<!-- Inside the field, outermost in its trailing band: these are
					     versions OF this name, and a trigger parked under the field read
					     as unrelated furniture. Always mounted, even on a type with no
					     stored row behind it — the trigger disables itself, so the
					     control is where the operator will look for it before there is
					     anything to look at. Locked while an unsaved edit stands: a pass
					     is written from the stored string, and this form's own Save
					     retires the result the moment it lands a different one. -->
					<localized-text
						slot="actions"
						label=${this.t("admin.events.localize.nameLabel")}
						.source=${stored?.name ?? ""}
						.byLocale=${stored?.nameByLocale ?? null}
						.multiline=${false}
						?pending=${namePassRunning}
						?disabled=${nameUnsaved}
						@save=${(e: LocalizedTextSaveEvent) =>
							this.storeCopy("type-name", e)}
						@translate=${(event: LocalizedTextTranslateEvent) => this.requestPass("type-name", event)}
					></localized-text>
				</schmancy-input>
				<!-- With no document behind the form (a copy, a brand-new type)
				     there is no stored string to retire, so the save-first
				     instruction would be vacuous — the notice falls to its own
				     nothing-stored line instead. -->
				<localize-notice
					.source=${stored?.name ?? ""}
					?unsaved=${stored !== undefined && nameUnsaved}
					?failed=${stored?.nameByLocaleStatus?.state === "failed"}
				></localize-notice>

				<!-- Optional, and the hint says so first. Nothing bounds the answer:
				     the storefront carries it as a clamped line under the type's
				     name and opens it in full on a tap, so length is a matter of
				     what an operator has to say rather than of what a row can
				     hold. -->
				<schmancy-textarea
					label=${this.t("admin.ticketTypes.form.descriptionLabel")}
					rows="2"
					hint=${this.t("admin.ticketTypes.form.descriptionHint")}
					.value=${this.description}
					@change=${(e: CustomEvent<{ value: string }>) => (this.description = e.detail.value)}
				>
					<localized-text
						slot="actions"
						multiline
						label=${this.t("admin.events.localize.descriptionLabel")}
						.source=${stored?.description ?? ""}
						.byLocale=${stored?.descriptionByLocale ?? null}
						?pending=${descriptionPassRunning}
						?disabled=${descriptionUnsaved}
						@save=${(e: LocalizedTextSaveEvent) =>
							this.storeCopy("type-description", e)}
						@translate=${(event: LocalizedTextTranslateEvent) => this.requestPass("type-description", event)}
					></localized-text>
				</schmancy-textarea>
				<localize-notice
					.source=${stored?.description ?? ""}
					?unsaved=${stored !== undefined && descriptionUnsaved}
					?failed=${stored?.descriptionByLocaleStatus?.state === "failed"}
				></localize-notice>

				<div class="grid grid-cols-2 gap-3">
					<schmancy-input
						label=${this.t("admin.ticketTypes.form.priceLabel")}
						type="text"
						inputmode="decimal"
						.value=${this.price}
						@change=${(e: CustomEvent<{ value: string }>) => (this.price = e.detail.value)}
					></schmancy-input>
					<schmancy-input
						label=${this.t("admin.ticketTypes.form.vatLabel")}
						type="number"
						min="0"
						max="100"
						hint=${this.t("admin.ticketTypes.form.vatHint")}
						.value=${this.vatRate}
						@change=${(e: CustomEvent<{ value: string }>) => (this.vatRate = e.detail.value)}
					></schmancy-input>
				</div>

				<schmancy-input
					label=${this.t("admin.ticketTypes.form.capacityLabel")}
					type="number"
					min="0"
					hint=${this.t("admin.ticketTypes.form.capacityHint")}
					.value=${this.capacity}
					@change=${(e: CustomEvent<{ value: string }>) => (this.capacity = e.detail.value)}
				></schmancy-input>

				<div class="flex flex-col gap-2">
					<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
						${this.t("admin.ticketTypes.form.claimableByLabel")}
					</schmancy-typography>
					<schmancy-chips
						class="w-full"
						required
						.value=${claimAudience}
						@change=${(e: CustomEvent<{ value: string }>) => {
							if (e.detail.value === claimAudience) return;
							this.claimableBy =
								e.detail.value === "staff"
									? "staff"
									: e.detail.value === "code"
										? "code"
										: "buyer";
						}}
					>
						<schmancy-filter-chip .value=${"buyer"}
							>${this.t("admin.ticketTypes.form.claimableByBuyer")}</schmancy-filter-chip
						>
						${when(
							!isDraftMode,
							() => html`
								<schmancy-filter-chip .value=${"code"}
									>${this.t("admin.ticketTypes.form.claimableByCode")}</schmancy-filter-chip
								>
							`,
						)}
						<schmancy-filter-chip .value=${"staff"}
							>${this.t("admin.ticketTypes.form.claimableByStaff")}</schmancy-filter-chip
						>
					</schmancy-chips>
					<!-- Every reachable state of the hint rendered into ONE grid
					     cell, all but the active one invisible, so switching
					     audience — or minting the first code that reveals this
					     type — swaps words inside a box whose height was fixed at
					     first paint. A hint that grows when the answer changes
					     moves every field under it, and this control sits two
					     thirds of the way up a long form. The stranded arm is not
					     a branch nested inside the code arm for the same reason:
					     a conditional inside an arm is the same defect one level
					     down. -->
					<div class="grid">
						${repeat(
							[
								{
									key: "buyer",
									active: claimAudience === "buyer",
									lines: [
										this.t(
											priceCents === 0
												? "admin.ticketTypes.form.claimableByBuyerFreeHint"
												: "admin.ticketTypes.form.claimableByBuyerHint",
										),
									],
									warning: null,
								},
								{
									key: "staff",
									active: claimAudience === "staff",
									lines: [
										this.t(
											priceCents === 0
												? "admin.ticketTypes.form.claimableByStaffFreeHint"
												: "admin.ticketTypes.form.claimableByStaffHint",
										),
									],
									warning: null,
								},
								{
									key: "code",
									active: claimAudience === "code" && unlockedBy.length > 0,
									lines: [
										this.t("admin.ticketTypes.form.claimableByCodeHint"),
										this.t("admin.ticketTypes.form.claimableByCodeUnlockedBy", {
											codes: unlockedBy.join(", "),
										}),
									],
									warning: null,
								},
								{
									key: "stranded",
									active: claimAudience === "code" && unlockedBy.length === 0,
									lines: [this.t("admin.ticketTypes.form.claimableByCodeHint")],
									warning: this.t(
										"admin.ticketTypes.form.claimableByCodeStranded",
									),
								},
							].filter(
								// A draft event holds no voucher, so the code arms can never
								// be reached there and reserve no height.
								(arm) =>
									!isDraftMode || arm.key === "buyer" || arm.key === "staff",
							),
							(arm) => arm.key,
							(arm) => html`
								<div
									class=${this.classMap({
										"col-start-1 row-start-1 flex flex-col gap-2": true,
										invisible: !arm.active,
									})}
									aria-hidden=${arm.active ? "false" : "true"}
								>
									${repeat(
										arm.lines,
										(line) => line,
										(line) => html`
											<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
												${line}
											</schmancy-typography>
										`,
									)}
									${when(
										arm.warning !== null,
										() => html`
											<schmancy-typography type="body" token="sm" class="text-error-default">
												${arm.warning}
											</schmancy-typography>
										`,
									)}
								</div>
							`,
						)}
					</div>
				</div>

				${when(
					!isDraftMode,
					() => html`
						<div class="flex flex-col gap-2">
							<schmancy-typography
								type="label"
								token="sm"
								class=${this.classMap({
									"text-surface-onVariant": !freeToAnyone,
									"text-primary-default": freeToAnyone,
								})}
							>
								${this.t("admin.ticketTypes.form.verifyAtDoorLabel")}
							</schmancy-typography>
							<schmancy-chips
								class="w-full"
								required
								.value=${this.verifyAtDoor ? "check" : "scan"}
								@change=${(e: CustomEvent<{ value: string }>) => {
									const next = e.detail.value === "check";
									if (next === this.verifyAtDoor) return;
									this.verifyAtDoor = next;
								}}
							>
								<schmancy-filter-chip .value=${"scan"}
									>${this.t("admin.ticketTypes.form.verifyAtDoorScan")}</schmancy-filter-chip
								>
								<schmancy-filter-chip .value=${"check"}
									>${this.t("admin.ticketTypes.form.verifyAtDoorCheck")}</schmancy-filter-chip
								>
							</schmancy-chips>
							<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
								${this.t(
									this.verifyAtDoor
										? "admin.ticketTypes.form.verifyAtDoorCheckHint"
										: "admin.ticketTypes.form.verifyAtDoorScanHint",
								)}
							</schmancy-typography>
							${when(
								this.verifyAtDoor,
								() => html`
									<schmancy-input
										label=${this.t("admin.ticketTypes.form.doorNoteLabel")}
										hint=${this.t("admin.ticketTypes.form.doorNoteHint")}
										.value=${this.doorNote}
										@change=${(e: CustomEvent<{ value: string }>) => {
											if (e.detail.value === this.doorNote) return;
											this.doorNote = e.detail.value;
										}}
									></schmancy-input>
								`,
							)}
						</div>
					`,
				)}

				<!-- Sits directly above the sales window on purpose: since the sales
				     resolver reads the entry close, an inheriting sale's label below
				     quotes the answer given here — and adjacency renders the
				     buy-vs-admit contrast in one glance. -->
				<div class="flex flex-col gap-2">
					<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
						${this.t("admin.ticketTypes.form.entryWindowLabel")}
					</schmancy-typography>
					<schmancy-chips
						class="w-full"
						required
						.value=${this.entryChoice}
						@change=${(e: CustomEvent<{ value: string }>) => {
							if (e.detail.value === this.entryChoice) return;
							this.entryChoice = e.detail.value === "days" ? "days" : "whole";
						}}
					>
						<schmancy-filter-chip .value=${"whole"}
							>${this.t("admin.ticketTypes.form.entryWholeEvent")}</schmancy-filter-chip
						>
						<schmancy-filter-chip .value=${"days"}
							>${this.t("admin.ticketTypes.form.entryCertainDays")}</schmancy-filter-chip
						>
					</schmancy-chips>
					${when(
						this.entryChoice === "whole",
						() => html`
							<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
								${this.t("admin.ticketTypes.form.entryWholeEventHint")}
							</schmancy-typography>
						`,
						() => html`
							${when(
								entryDays.length === 0,
								() => html`
									<div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
										<schmancy-date-field
											type="datetime-local"
											label=${this.t("admin.ticketTypes.form.entryFromLabel")}
											.value=${this.entryOpensAt}
											@change=${(e: CustomEvent<{ value: string }>) => {
												if (e.detail.value === this.entryOpensAt) return;
												this.entryOpensAt = e.detail.value;
											}}
										></schmancy-date-field>
										<schmancy-date-field
											type="datetime-local"
											label=${this.t("admin.ticketTypes.form.entryToLabel")}
											.value=${this.entryClosesAt}
											@change=${(e: CustomEvent<{ value: string }>) => {
												if (e.detail.value === this.entryClosesAt) return;
												this.entryClosesAt = e.detail.value;
											}}
										></schmancy-date-field>
									</div>
								`,
								() => html`
									<schmancy-chips
										class="w-full"
										multi
										wrap
										role="group"
										aria-label=${this.t("admin.ticketTypes.form.entryDaysGroupAriaLabel")}
										.value=${[...entryRange]}
										@change=${(e: CustomEvent<{ value: string[] }>) => {
											// The group is a plain toggle-set: it mutates and emits
											// before this runs, its array is insertion-ordered, and
											// `required` carries no deselect guard on the multi
											// branch at all. So the emission says only WHICH chip
											// moved — in either direction, since tapping a lit chip
											// removes it — and the span on screen stays the truth,
											// written back sorted and contiguous.
											const emitted = new Set(e.detail.value);
											const tapped =
												e.detail.value.find(
													(day) => !entryRange.includes(day),
												) ?? entryRange.find((day) => !emitted.has(day));
											if (tapped === undefined) return;
											if (
												this.entryPicking === "first" ||
												entryOpensDay === ""
											) {
												this.setEntryWindow(tapped, tapped, entryFrom, entryTo);
												this.entryPicking = "last";
												return;
											}
											this.setEntryWindow(
												tapped < entryOpensDay ? tapped : entryOpensDay,
												tapped < entryOpensDay ? entryOpensDay : tapped,
												entryFrom,
												entryTo,
											);
											this.entryPicking = "first";
										}}
									>
										${repeat(
											entryDays,
											(day) => day,
											(day) => {
												const part = (opts: Intl.DateTimeFormatOptions) =>
													new Date(`${day}T00:00:00.000Z`).toLocaleDateString(
														this.locale,
														{
															...opts,
															timeZone: "UTC",
														},
													);
												return html`
													<schmancy-filter-chip size="lg" .value=${day}>
														<span class="flex w-9 flex-col items-center py-2" aria-hidden="true">
															<schmancy-typography type="label" token="xs" transform="uppercase" class="tracking-widest">
																${part({ weekday: "short" })}
															</schmancy-typography>
															<schmancy-typography type="title" token="lg" class="tabular-nums">
																${part({ day: "2-digit" })}
															</schmancy-typography>
															<schmancy-typography type="label" token="xs" transform="uppercase" class="tracking-widest">
																${part({ month: "short" })}
															</schmancy-typography>
														</span>
														<schmancy-visually-hidden>
															${this.t(
																"admin.ticketTypes.form.entryDayAriaLabel",
																{
																	day: part({
																		weekday: "long",
																		day: "numeric",
																		month: "long",
																	}),
																},
															)}
														</schmancy-visually-hidden>
													</schmancy-filter-chip>
												`;
											},
										)}
									</schmancy-chips>
									<!-- Speaks while the row is empty and while a second tap is
									     outstanding, then yields to the sentence below: over a
									     settled span the same line reads as "nothing is picked"
									     with that span lit underneath. It sits UNDER the row
									     because it is the only thing here whose height changes
									     mid-gesture — the second sentence is the longer one and
									     wraps at a phone's measure, so above the row it would push
									     the chips down between the first tap and the second, under
									     a finger already reaching for the target. -->
									${when(
										entryRange.length === 0 || this.entryPicking === "last",
										() => html`
											<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
												${this.t(
													entryRange.length === 0
														? "admin.ticketTypes.form.entryPickFirstDay"
														: "admin.ticketTypes.form.entryPickLastDay",
												)}
											</schmancy-typography>
										`,
									)}
									${when(
										entryRange.length > 0,
										() => html`
											<div class="flex flex-wrap gap-3 pt-1">
												<schmancy-input
													class="min-w-40 flex-1"
													type="time"
													label=${this.t(
														"admin.ticketTypes.form.entryFromLabelOnDay",
														{
															day: entryDayLabel(entryOpensDay),
														},
													)}
													.value=${entryFrom}
													@change=${(e: CustomEvent<{ value: string }>) => {
														if (e.detail.value === entryFrom) return;
														this.setEntryWindow(
															entryOpensDay,
															entryLastDay,
															e.detail.value,
															entryTo,
														);
													}}
												></schmancy-input>
												<schmancy-input
													class="min-w-40 flex-1"
													type="time"
													label=${this.t(
														"admin.ticketTypes.form.entryToLabelOnDay",
														{
															day: entryDayLabel(
																this.entryClosesAt.slice(0, 10),
															),
														},
													)}
													.value=${entryTo}
													@change=${(e: CustomEvent<{ value: string }>) => {
														if (e.detail.value === entryTo) return;
														this.setEntryWindow(
															entryOpensDay,
															entryLastDay,
															entryFrom,
															e.detail.value,
														);
													}}
												></schmancy-input>
											</div>
										`,
									)}
								`,
							)}
							${when(
								this.entryOpensAt !== "" && this.entryClosesAt !== "",
								() => html`
									<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
										${
											entryOpensDay === this.entryClosesAt.slice(0, 10)
												? this.t(
														entryZoneKnown
															? "admin.ticketTypes.form.entrySummaryOneDay"
															: "admin.ticketTypes.form.entrySummaryOneDayNoZone",
														{
															day: entryDayLabel(entryOpensDay),
															from: entryFrom,
															to: entryTo,
															tz,
														},
													)
												: this.t(
														entryZoneKnown
															? "admin.ticketTypes.form.entrySummarySpan"
															: "admin.ticketTypes.form.entrySummarySpanNoZone",
														{
															start: entryDayLabel(entryOpensDay),
															end: entryDayLabel(
																this.entryClosesAt.slice(0, 10),
															),
															from: entryFrom,
															to: entryTo,
															tz,
														},
													)
										}
									</schmancy-typography>
								`,
							)}
						`,
					)}
				</div>

				<div class="flex flex-col gap-2">
					<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
						${this.t("admin.ticketTypes.form.salesWindowLabel")}
					</schmancy-typography>
					<div class="flex items-center gap-2">
						<schmancy-switch
							.checked=${this.inheritSalesWindow}
							aria-label=${this.t("admin.ticketTypes.form.salesFollowEvent")}
							@change=${(e: CustomEvent<{ value: boolean }>) => {
								if (e.detail.value === this.inheritSalesWindow) return;
								this.inheritSalesWindow = e.detail.value;
							}}
						></schmancy-switch>
						<schmancy-typography type="label" token="sm">
							${this.t(
								this.inheritSalesWindow
									? "admin.ticketTypes.form.salesFollowEvent"
									: "admin.ticketTypes.form.ownSalesWindow",
							)}
						</schmancy-typography>
					</div>
					${when(
						this.inheritSalesWindow,
						() => html`
							<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
								${
									isDraftMode
										? this.t("admin.ticketTypes.form.salesFollowEventHintDraft")
										: html`${this.inheritedWindowLabel() ?? this.t("admin.ticketTypes.form.fallbackWindowLabel")}
											${this.t("admin.ticketTypes.form.salesFollowEventSuffix")}`
								}
							</schmancy-typography>
						`,
						() => html`
							<!-- Scheduling looks ahead: schmancy's default chips are reporting
							     ranges (Yesterday, Last 7 days) that land in the past. -->
							<schmancy-date-range
								type="datetime-local"
								hide-nav
								.presets=${[]}
								placeholder=${this.t("admin.ticketTypes.form.salesWindowPlaceholder")}
								.start=${this.opensAt || null}
								.end=${this.closesAt || null}
								@change=${(
									e: CustomEvent<{
										start: string | null;
										end: string | null;
									}>,
								) => {
									this.opensAt = e.detail.start ?? "";
									this.closesAt = e.detail.end ?? "";
								}}
							></schmancy-date-range>
							<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
								${this.t("admin.ticketTypes.form.ownWindowHint")}
							</schmancy-typography>
						`,
					)}
					${when(
						salesClosesAfterEntryEnds,
						() => html`
							<schmancy-typography type="body" token="sm" class="text-error-default">
								${this.t("admin.ticketTypes.form.salesAfterEntryWarning")}
							</schmancy-typography>
						`,
					)}
				</div>

				<div class="flex flex-col gap-2">
					<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
						${this.t("admin.ticketTypes.form.releaseLabel")}
					</schmancy-typography>
					<div class="flex items-center gap-2">
						<schmancy-switch
							.checked=${this.releaseCondition === "always"}
							aria-label=${this.t("admin.ticketTypes.form.releaseAlways")}
							@change=${(e: CustomEvent<{ value: boolean }>) => {
								const next = e.detail.value ? "always" : "afterSoldOut";
								if (next === this.releaseCondition) return;
								this.releaseCondition = next;
								if (next === "always") this.showAfterSoldOut = "";
							}}
						></schmancy-switch>
						<schmancy-typography type="label" token="sm">
							${this.t(
								this.releaseCondition === "always"
									? "admin.ticketTypes.form.releaseAlways"
									: "admin.ticketTypes.form.releaseAfterSoldOut",
							)}
						</schmancy-typography>
					</div>
					${when(
						this.releaseCondition === "afterSoldOut",
						() => html`
							${when(
								chainCandidates.length === 0,
								() => html`
									<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
										${this.t("admin.ticketTypes.form.noChainCandidates")}
									</schmancy-typography>
								`,
								() => html`
									<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
										${this.t("admin.ticketTypes.form.chainPrompt")}
									</schmancy-typography>
									<schmancy-chips
										class="w-full"
										required
										.value=${this.showAfterSoldOut}
										@change=${(e: CustomEvent<{ value: string }>) => {
											if (e.detail.value === this.showAfterSoldOut) return;
											this.showAfterSoldOut = e.detail.value;
										}}
									>
										${repeat(
											chainCandidates,
											(c) => c.id,
											(c) =>
												html`<schmancy-filter-chip .value=${c.id}>${c.name}</schmancy-filter-chip>`,
										)}
									</schmancy-chips>
								`,
							)}
						`,
					)}
				</div>

				${when(
					priceDescends,
					() => html`
						<schmancy-typography type="body" token="sm" class="text-error-default">
							${this.t("admin.ticketTypes.form.priceDescendsWarning")}
						</schmancy-typography>
					`,
				)}
				${when(
					predecessorHasClosesAt,
					() => html`
						<schmancy-typography type="body" token="sm" class="text-error-default">
							${this.t("admin.ticketTypes.form.predecessorClosesAtWarning")}
						</schmancy-typography>
					`,
				)}

				<select-location
					label=${this.t("admin.ticketTypes.form.whereLabel")}
					emptyHint=${this.t("admin.ticketTypes.form.venueEmptyHint")}
					emptyLabel=${this.t("admin.ticketTypes.form.useEventVenue")}
					.value=${this.locationId}
					@change=${(e: CustomEvent<{ value: string }>) =>
						(this.locationId = e.detail.value)}
				></select-location>

				<div class="flex flex-col gap-2">
					<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
						${this.t("admin.ticketTypes.form.templateOverrideLabel")}
					</schmancy-typography>
					<ticket-template-picker
						.value=${this.templateVariant}
						.defaultVariant=${event?.templateVariant ?? DEFAULT_TICKET_TEMPLATE_VARIANT}
						label=${this.t("admin.ticketTypes.form.templateOverrideLabel")}
						@change=${(e: CustomEvent<{ value: TicketTemplateVariant | null }>) => (this.templateVariant = e.detail.value)}
					></ticket-template-picker>
				</div>

				</div>

				<!-- Wraps, so a phone's width stacks the two groups instead of
				     squeezing four buttons onto one line. -->
				<schmancy-surface opacity="translucent" class="flex flex-wrap items-center gap-3 px-4 pb-4 pt-2">
					${when(
						editing,
						() => html`
							<div class="flex flex-wrap gap-2">
								${when(
									showDeleteButton,
									() => html`
										<schmancy-button variant="outlined" color="error" ?disabled=${this.saving && this.activeAction !== "delete"} .busy=${this.saving && this.activeAction === "delete"} @click=${() => this.deleteTicketType()}>
											<span style="display:grid"><span style=${this.failedAction === "delete" ? "grid-area:1/1;visibility:hidden" : "grid-area:1/1"} aria-hidden=${this.failedAction === "delete" ? "true" : "false"}>${this.t("admin.common.delete")}</span><span style=${this.failedAction === "delete" ? "grid-area:1/1" : "grid-area:1/1;visibility:hidden"} aria-hidden=${this.failedAction === "delete" ? "false" : "true"}>${this.t("admin.common.retryDelete")}</span></span>
										</schmancy-button>
									`,
								)}
								<schmancy-button variant="outlined" ?disabled=${this.saving} @click=${() => this.duplicate()}>
									<schmancy-icon slot="prefix" aria-hidden="true">content_copy</schmancy-icon>
									${this.t("admin.ticketTypes.form.duplicate")}
								</schmancy-button>
							</div>
						`,
					)}
					<div class="ml-auto flex flex-wrap justify-end gap-2">
						<schmancy-button variant="outlined" ?disabled=${this.saving} @click=${() => this.close()}>${this.t("admin.common.cancel")}</schmancy-button>
						<schmancy-button variant="outlined" ?disabled=${!canSave || (this.saving && this.activeAction !== "save")} .busy=${this.saving && this.activeAction === "save"} color=${this.failedAction === "save" ? "error" : "primary"} @click=${() => this.save()}>
							<span style="display:grid"><span style=${this.failedAction === "save" ? "grid-area:1/1;visibility:hidden" : "grid-area:1/1"} aria-hidden=${this.failedAction === "save" ? "true" : "false"}>${this.t("admin.common.save")}</span><span style=${this.failedAction === "save" ? "grid-area:1/1" : "grid-area:1/1;visibility:hidden"} aria-hidden=${this.failedAction === "save" ? "false" : "true"}>${this.t("admin.common.retrySave")}</span></span>
						</schmancy-button>
					</div>
				</schmancy-surface>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"ticket-type-form": TicketTypeForm;
	}
}
